import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { FileText, RefreshCw, TrendingDown, TrendingUp, Wallet, AlertCircle } from "lucide-react";

const todayISO = () => new Date().toISOString().slice(0, 10);
const offsetISO = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};
const monthStartISO = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};
const lastMonthRangeISO = () => {
  const d = new Date();
  const start = new Date(d.getFullYear(), d.getMonth() - 1, 1);
  const end = new Date(d.getFullYear(), d.getMonth(), 0);
  return [start.toISOString().slice(0, 10), end.toISOString().slice(0, 10)];
};
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

// Default: current month (1st → today).
const DEFAULT_START = monthStartISO();
const DEFAULT_END = todayISO();

const PRESETS = [
  { key: "last7", label: "Last 7 days", fn: () => [offsetISO(-6), todayISO()] },
  { key: "this_month", label: "This month", fn: () => [monthStartISO(), todayISO()] },
  { key: "last_month", label: "Last month", fn: () => lastMonthRangeISO() },
  { key: "last30", label: "Last 30 days", fn: () => [offsetISO(-29), todayISO()] },
  { key: "ytd", label: "This year", fn: () => [`${new Date().getFullYear()}-01-01`, todayISO()] },
];

export default function PayoutsTab({ onViewLedger }) {
  const [start, setStart] = useState(DEFAULT_START);
  const [end, setEnd] = useState(DEFAULT_END);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [onlyDues, setOnlyDues] = useState(false);
  const [q, setQ] = useState("");

  const load = async () => {
    if (!start || !end) return;
    if (start > end) {
      toast.error("Start date must be on or before end date");
      return;
    }
    setLoading(true);
    try {
      const res = await api.get("/staff/procurement/payouts", { params: { start_date: start, end_date: end } });
      setReport(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load payouts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const applyPreset = (fn) => {
    const [s, e] = fn();
    setStart(s); setEnd(e);
    // fire load on next tick so state is committed
    setTimeout(() => load(), 0);
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/staff/procurement/payouts.pdf", {
        params: { start_date: start, end_date: end },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `TBB-Payouts-${start}-to-${end}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "PDF download failed");
    } finally {
      setDownloading(false);
    }
  };

  const summary = report?.summary || {};
  const filteredVendors = useMemo(() => {
    const rows = report?.vendors || [];
    const term = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (onlyDues && Number(r.outstanding) <= 0) return false;
      if (term && !((r.vendor_name || "").toLowerCase().includes(term))) return false;
      return true;
    });
  }, [report, onlyDues, q]);

  return (
    <div data-testid="payouts-tab">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow">Payouts</div>
          <h2 className="mt-1 text-3xl">Vendor payables snapshot</h2>
          <p className="mt-1 text-sm text-[hsl(var(--ink-soft))] max-w-2xl">
            Pick a date range to see GRN value received, payments made, and current outstanding across all vendors. Outstanding is always the live position — not window-bound.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="payouts-refresh" onClick={load} disabled={loading} className="inline-flex items-center gap-2 px-4 py-2.5 text-sm border border-[hsl(var(--rule))] hover:border-[hsl(var(--forest))] disabled:opacity-40">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> {loading ? "Loading…" : "Refresh"}
          </button>
          <button data-testid="payouts-pdf" onClick={downloadPdf} disabled={downloading || !report} className="inline-flex items-center gap-2 px-4 py-2.5 text-sm border border-[hsl(var(--forest))] text-[hsl(var(--forest))] hover:bg-[hsl(var(--forest))] hover:text-[hsl(var(--bg))] disabled:opacity-40">
            <FileText size={14} /> {downloading ? "Preparing…" : "Download PDF"}
          </button>
        </div>
      </div>

      {/* Date range controls */}
      <div className="mt-6 p-4 border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))]">
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
            From
            <input data-testid="payouts-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="block mt-1 tbb-inp" />
          </label>
          <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
            To
            <input data-testid="payouts-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="block mt-1 tbb-inp" />
          </label>
          <button data-testid="payouts-apply" onClick={load} disabled={loading} className="btn-brass !py-2.5 !px-5 text-sm">
            {loading ? "Loading…" : "Apply"}
          </button>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-wider text-[hsl(var(--ink-soft))] mr-1">Presets</span>
          {PRESETS.map((p) => (
            <button
              key={p.key}
              data-testid={`payouts-preset-${p.key}`}
              onClick={() => applyPreset(p.fn)}
              className="text-xs px-2.5 py-1 border border-[hsl(var(--rule))] hover:border-[hsl(var(--brass))] hover:text-[hsl(var(--brass-2))] rounded-full"
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary tiles */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile
          testid="payouts-tile-grn"
          label="GRN in range"
          value={inr(summary.grn_in_range)}
          icon={TrendingUp}
          sub={`${report?.vendors?.reduce((n, r) => n + (r.grn_count_in_range || 0), 0) || 0} receipts`}
        />
        <Tile
          testid="payouts-tile-paid"
          label="Paid in range"
          value={inr(summary.paid_in_range)}
          icon={TrendingDown}
          sub={`${report?.vendors?.reduce((n, r) => n + (r.payment_count_in_range || 0), 0) || 0} payments`}
          accent="forest"
        />
        <Tile
          testid="payouts-tile-outstanding"
          label="Total outstanding"
          value={inr(summary.outstanding)}
          icon={Wallet}
          sub={`${summary.vendors_with_dues || 0} vendor${(summary.vendors_with_dues || 0) === 1 ? "" : "s"} with dues`}
          accent={Number(summary.outstanding) > 0 ? "red" : "forest"}
        />
        <Tile
          testid="payouts-tile-net"
          label="Net change"
          value={inr(summary.net_change_in_range)}
          icon={AlertCircle}
          sub={Number(summary.net_change_in_range) > 0 ? "Payables ↑" : Number(summary.net_change_in_range) < 0 ? "Payables ↓" : "Even"}
          accent={Number(summary.net_change_in_range) > 0 ? "brass" : "forest"}
        />
      </div>

      {/* Filters */}
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <input
          data-testid="payouts-search"
          type="search"
          placeholder="Search vendor…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="tbb-inp w-64"
        />
        <label className="inline-flex items-center gap-2 text-sm text-[hsl(var(--ink-soft))] cursor-pointer">
          <input data-testid="payouts-only-dues" type="checkbox" checked={onlyDues} onChange={(e) => setOnlyDues(e.target.checked)} />
          Only vendors with dues
        </label>
        <div className="ml-auto text-xs text-[hsl(var(--ink-soft))]">
          Showing {filteredVendors.length} of {report?.vendors?.length || 0}
        </div>
      </div>

      {/* Vendor breakdown table */}
      <div className="mt-3 border border-[hsl(var(--rule))] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[hsl(var(--bg-2))] text-[10px] uppercase tracking-wider text-[hsl(var(--ink-soft))] sticky top-0 z-10 shadow-[0_1px_0_hsl(var(--rule))]">
            <tr>
              <th className="p-3 text-left">Vendor</th>
              <th className="p-3 text-left">Category</th>
              <th className="p-3 text-right">GRN in range</th>
              <th className="p-3 text-right">Paid in range</th>
              <th className="p-3 text-right">Outstanding</th>
              <th className="p-3 text-left">Last GRN</th>
              <th className="p-3 text-left">Last paid</th>
              <th className="p-3 text-right"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[hsl(var(--rule))]">
            {loading && (
              <tr><td colSpan={8} className="p-8 text-center text-sm text-[hsl(var(--ink-soft))]">Loading…</td></tr>
            )}
            {!loading && filteredVendors.length === 0 && (
              <tr><td colSpan={8} className="p-10 text-center text-sm text-[hsl(var(--ink-soft))]">
                No vendor activity matches these filters.
              </td></tr>
            )}
            {!loading && filteredVendors.map((r) => {
              const outVal = Number(r.outstanding || 0);
              const outClass = outVal > 0 ? "text-[hsl(6_62%_45%)]" : outVal < 0 ? "text-[hsl(var(--forest))]" : "text-[hsl(var(--ink))]";
              return (
                <tr key={r.vendor_id} data-testid={`payouts-row-${r.vendor_id}`}>
                  <td className="p-3">
                    <div className="font-semibold">{r.vendor_name}{!r.active && <span className="ml-1 text-[9px] uppercase text-[hsl(var(--ink-soft))]">(inactive)</span>}</div>
                    {r.phone && <div className="text-xs text-[hsl(var(--ink-soft))]">{r.phone}</div>}
                    {r.payment_terms && <div className="text-[10px] text-[hsl(var(--brass-2))]">{r.payment_terms}</div>}
                  </td>
                  <td className="p-3 text-xs text-[hsl(var(--ink-soft))] capitalize">{r.category || "—"}</td>
                  <td className="p-3 text-right">{inr(r.grn_in_range)}</td>
                  <td className="p-3 text-right text-[hsl(var(--forest))]">{inr(r.paid_in_range)}</td>
                  <td className={`p-3 text-right font-semibold ${outClass}`}>
                    {outVal < 0 ? `-${inr(Math.abs(outVal))}` : inr(outVal)}
                  </td>
                  <td className="p-3 text-xs text-[hsl(var(--ink-soft))]">{r.last_grn_date || "—"}</td>
                  <td className="p-3 text-xs text-[hsl(var(--ink-soft))]">{r.last_payment_date || "—"}</td>
                  <td className="p-3 text-right">
                    <button
                      data-testid={`payouts-view-ledger-${r.vendor_id}`}
                      onClick={() => onViewLedger && onViewLedger(r.vendor_id)}
                      className="text-xs text-[hsl(var(--brass-2))] hover:underline"
                    >
                      Ledger →
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
          {!loading && filteredVendors.length > 0 && (
            <tfoot className="bg-[hsl(var(--bg-2))] text-sm font-semibold">
              <tr>
                <td className="p-3">TOTAL</td>
                <td></td>
                <td className="p-3 text-right">{inr(filteredVendors.reduce((s, r) => s + Number(r.grn_in_range || 0), 0))}</td>
                <td className="p-3 text-right text-[hsl(var(--forest))]">{inr(filteredVendors.reduce((s, r) => s + Number(r.paid_in_range || 0), 0))}</td>
                <td className="p-3 text-right">{inr(filteredVendors.reduce((s, r) => s + Number(r.outstanding || 0), 0))}</td>
                <td colSpan={3}></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <style>{`.tbb-inp{background:hsl(var(--bg));border:1px solid hsl(var(--rule));padding:0.55rem 0.75rem;font-size:0.9rem}.tbb-inp:focus{outline:1px solid hsl(var(--forest));border-color:hsl(var(--forest))}`}</style>
    </div>
  );
}

function Tile({ label, value, sub, testid, accent = "ink", icon: Icon }) {
  const color = {
    ink: "text-[hsl(var(--ink))]",
    forest: "text-[hsl(var(--forest))]",
    brass: "text-[hsl(var(--brass-2))]",
    red: "text-[hsl(6_62%_45%)]",
  }[accent] || "text-[hsl(var(--ink))]";
  return (
    <div data-testid={testid} className="border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))] p-4">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--ink-soft))]">{label}</div>
        {Icon && <Icon size={14} className="text-[hsl(var(--brass))]" />}
      </div>
      <div className={`mt-1 text-2xl font-semibold ${color}`}>{value}</div>
      {sub && <div className="mt-1 text-[10px] text-[hsl(var(--ink-soft))]">{sub}</div>}
    </div>
  );
}
